import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CircleDollarSign, AlertCircle, WalletCards } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RevenueChart } from "@/components/reporting/RevenueChart";
import { useFinancialOverview } from "@/hooks/useFinancialOverview";
import { useStripeBalance } from "@/hooks/stripe/useStripeBalance";
import { toast } from "sonner";

// Subcomponent for Stripe Balance tab, only loaded when needed
const StripeBalanceTab: React.FC<{ formatCurrency: (cents: number) => string }> = ({ formatCurrency }) => {
  const { data: stripeBalance, isError } = useStripeBalance();
  if (isError) {
    toast.error("Failed to load Stripe balance");
  }
  return (
    <div className="grid grid-cols-2 gap-4">
      {stripeBalance?.isTestMode && (
        <div className="col-span-2 bg-amber-50 border border-amber-200 p-3 rounded-lg mb-2">
          <p className="text-amber-700 text-sm font-medium flex items-center">
            <AlertCircle className="h-4 w-4 mr-2" />
            Test Mode Active
          </p>
          <p className="text-amber-600 text-xs mt-1">
            This is a test account. No real transactions will be processed.
          </p>
        </div>
      )}
      <div className="bg-slate-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <WalletCards className="h-5 w-5 text-green-500" />
          <p className="text-sm font-medium">Available Balance</p>
        </div>
        <p className="text-2xl font-bold text-green-600">
          {stripeBalance ? formatCurrency(stripeBalance.available) : '--'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Ready to withdraw to your bank
        </p>
      </div>
      <div className="bg-slate-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <CircleDollarSign className="h-5 w-5 text-blue-500" />
          <p className="text-sm font-medium">Pending Balance</p>
        </div>
        <p className="text-2xl font-bold text-blue-600">
          {stripeBalance ? formatCurrency(stripeBalance.pending) : '--'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Will be available soon
        </p>
      </div>
    </div>
  );
};

const FinancialOverview: React.FC = () => {
  const { data: financialData, isLoading } = useFinancialOverview();
  const [activeTab, setActiveTab] = useState('revenue-trend');

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  if (isLoading) {
    return (
      <Card className="shadow-sm border-gray-100/40 bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center">
              <CircleDollarSign className="h-5 w-5 mr-2 text-emerald-500" />
              Financial Overview
            </CardTitle>
            <Tabs defaultValue="monthly" className="h-8">
              <TabsList className="h-8 p-0.5">
                <TabsTrigger value="monthly" className="text-xs h-7">Monthly</TabsTrigger>
                <TabsTrigger value="quarterly" className="text-xs h-7">Quarterly</TabsTrigger>
                <TabsTrigger value="yearly" className="text-xs h-7">Yearly</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <Tabs defaultValue="revenue-trend">
            <TabsList className="w-full mb-4">
              <TabsTrigger value="revenue-trend" className="flex-1">Revenue Trend</TabsTrigger>
              <TabsTrigger value="payment-status" className="flex-1">Payment Status</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-gray-100/40 bg-white/80 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center">
            <CircleDollarSign className="h-5 w-5 mr-2 text-emerald-500" />
            Financial Overview
          </CardTitle>
          <Tabs defaultValue="monthly" className="h-8">
            <TabsList className="h-8 p-0.5">
              <TabsTrigger value="monthly" className="text-xs h-7">Monthly</TabsTrigger>
              <TabsTrigger value="quarterly" className="text-xs h-7">Quarterly</TabsTrigger>
              <TabsTrigger value="yearly" className="text-xs h-7">Yearly</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="revenue-trend">
          <TabsList className="w-full mb-4">
            <TabsTrigger value="revenue-trend" className="flex-1">Revenue Trend</TabsTrigger>
            <TabsTrigger value="payment-status" className="flex-1">Payment Status</TabsTrigger>
            <TabsTrigger value="stripe-balance" className="flex-1">Stripe Balance</TabsTrigger>
          </TabsList>
          <TabsContent value="revenue-trend">
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Current Month</p>
                <p className="text-xl font-bold">{formatCurrency(financialData?.currentMonthRevenue || 0)}</p>
                <p className="text-xs font-medium text-emerald-500">{financialData?.percentChange.toFixed(1)}%</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Prev. Month</p>
                <p className="text-xl font-bold">{formatCurrency(financialData?.previousMonthRevenue || 0)}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Avg Per Student</p>
                <p className="text-xl font-bold">{formatCurrency(financialData?.avgRevenuePerStudent || 0)}</p>
              </div>
            </div>
            
            <div className="h-56">
              <RevenueChart />
            </div>
          </TabsContent>
          
          <TabsContent value="payment-status">
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Outstanding</p>
                <p className="text-xl font-bold">{formatCurrency(financialData?.outstandingPayments || 0)}</p>
                <p className="text-xs">Total unpaid invoices</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg flex">
                <div className="mr-3">
                  <AlertCircle className="h-8 w-8 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Overdue</p>
                  <p className="text-xl font-bold text-amber-500">{formatCurrency(financialData?.overduePayments || 0)}</p>
                  <p className="text-xs">Requires attention</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Top Outstanding Invoices</h4>
              {financialData?.topOutstandingInvoices.map((invoice, i) => (
                <div key={invoice.id} className="flex justify-between items-center p-2 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{invoice.familyName}</p>
                    <p className="text-xs text-muted-foreground">Invoice #{invoice.id.slice(0, 8)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(invoice.amount)}</p>
                    <p className="text-xs text-amber-500">Due in {invoice.dueIn} days</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="stripe-balance">
            {activeTab === 'stripe-balance' && (
              <StripeBalanceTab formatCurrency={formatCurrency} />
            )}
            <p className="text-sm text-muted-foreground mt-4">
              Your available balance can be withdrawn to your connected bank account. 
              Pending funds will become available after the standard payout waiting period.
            </p>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default FinancialOverview;
